import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { ChevronDown, ChevronRight, User2, Users2, TrendingUp, DollarSign, Layers } from "lucide-react";

const RANK_COLORS = { core: "#22C55E", prime: "#3B82F6", elite: "#A855F7", supreme: "#EAB308" };

// Commission % by level (mirrors backend: Level 1-5 fixed + Level 6+ = 0.5% each)
const LEVEL_PCT = { 1: 10, 2: 5, 3: 3, 4: 2, 5: 1 };
const levelPct = (lv) => LEVEL_PCT[lv] ?? 0.5;

function flatten(root) {
  const rows = [];
  const walk = (n, level, parents) => {
    rows.push({ node: n, level, parentIds: parents });
    (n.children || []).forEach((c) => walk(c, level + 1, [...parents, n.id]));
  };
  if (root) walk(root, 0, []);
  return rows;
}

function KPI({ label, value, icon: Icon, color = "#00E5FF" }) {
  return (
    <div className="nb-card p-4">
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-400">
        <span>{label}</span>
        {Icon && <Icon size={14} style={{ color }}/>}
      </div>
      <div className="mt-2 font-mono text-2xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

export default function TeamTree() {
  const [tree, setTree] = useState(null);
  const [stats, setStats] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [view, setView] = useState("levels"); // "levels" | "tree"

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([api.get("/team/tree"), api.get("/team/stats")]);
        setTree(t.data.tree);
        setStats(s.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const rows = useMemo(() => flatten(tree), [tree]);
  const isHidden = (row) => row.parentIds.some((pid) => collapsed[pid]);
  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const levels = stats?.levels || [];

  return (
    <AppLayout variant="user">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Downline · Unlimited depth</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">My Team</h1>
        <p className="text-slate-400 mt-2 text-sm">Direct referral 10% (one-time on first activation) · Level 1-5: 10 / 5 / 3 / 2 / 1% · Level 6+: 0.5% each · unlimited depth.</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI label="Total Members" value={String(stats?.total_team_size ?? 0)} icon={Users2} color="#A855F7"/>
        <KPI label="Active Members" value={String(stats?.total_active_team ?? 0)} icon={Users2} color="#8AE234"/>
        <KPI label="Team Volume" value={`$${Number(stats?.total_team_volume ?? 0).toFixed(2)}`} icon={TrendingUp} color="#00E5FF"/>
        <KPI label="Commission Earned" value={`$${Number(stats?.total_team_commission ?? 0).toFixed(2)}`} icon={DollarSign} color="#EAB308"/>
      </div>

      {/* Toggle */}
      <div className="flex gap-2 mb-4">
        <button data-testid="team-view-levels" onClick={() => setView("levels")}
          className={`text-xs px-3 py-2 rounded-lg border ${view==="levels" ? "border-nb-cyan bg-nb-cyan/10 text-nb-cyan" : "border-white/10 bg-white/5 text-slate-300 hover:border-white/30"}`}>
          <Layers size={12} className="inline mr-1"/> Level breakdown
        </button>
        <button data-testid="team-view-tree" onClick={() => setView("tree")}
          className={`text-xs px-3 py-2 rounded-lg border ${view==="tree" ? "border-nb-cyan bg-nb-cyan/10 text-nb-cyan" : "border-white/10 bg-white/5 text-slate-300 hover:border-white/30"}`}>
          <User2 size={12} className="inline mr-1"/> Team tree
        </button>
      </div>

      {/* Level breakdown view */}
      {view === "levels" && (
        <div className="nb-card p-4 overflow-x-auto" data-testid="team-levels-table">
          {levels.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-10">No downline yet. Share your referral code to grow your team.</div>
          ) : (
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-white/10">
                  <th className="py-3 pr-3">Level</th>
                  <th className="py-3 pr-3">Rate</th>
                  <th className="py-3 pr-3 text-right">Members</th>
                  <th className="py-3 pr-3 text-right">Active</th>
                  <th className="py-3 pr-3 text-right">Volume</th>
                  <th className="py-3 pr-3 text-right">Commission Earned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {levels.map((lv) => (
                  <tr key={lv.level} data-testid={`team-level-${lv.level}`} className="hover:bg-white/5">
                    <td className="py-3 pr-3">
                      <div className="text-white font-bold">Level {lv.level}</div>
                      {lv.level === 1 && <div className="text-[10px] text-nb-cyan">Direct referrals</div>}
                      {lv.level >= 6 && <div className="text-[10px] text-slate-500">Deep · unlimited</div>}
                    </td>
                    <td className="py-3 pr-3 text-nb-cyan font-mono text-xs">{levelPct(lv.level)}%</td>
                    <td className="py-3 pr-3 text-right font-mono text-white">{lv.total_members}</td>
                    <td className="py-3 pr-3 text-right font-mono text-[#8AE234]">{lv.active_members}</td>
                    <td className="py-3 pr-3 text-right font-mono text-slate-300">${Number(lv.volume).toFixed(2)}</td>
                    <td className="py-3 pr-3 text-right font-mono text-[#EAB308]">${Number(lv.commission_earned).toFixed(4)}</td>
                  </tr>
                ))}
                <tr className="bg-white/5 font-bold">
                  <td className="py-3 pr-3 text-white">Total</td>
                  <td className="py-3 pr-3"></td>
                  <td className="py-3 pr-3 text-right font-mono text-white">{stats?.total_team_size ?? 0}</td>
                  <td className="py-3 pr-3 text-right font-mono text-[#8AE234]">{stats?.total_active_team ?? 0}</td>
                  <td className="py-3 pr-3 text-right font-mono text-white">${Number(stats?.total_team_volume ?? 0).toFixed(2)}</td>
                  <td className="py-3 pr-3 text-right font-mono text-[#EAB308]">${Number(stats?.total_team_commission ?? 0).toFixed(4)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tree view */}
      {view === "tree" && (
        <div className="nb-card p-6 overflow-x-auto" data-testid="team-tree">
          {!tree ? (
            <div className="text-sm text-slate-500 text-center py-6">Loading…</div>
          ) : rows.filter(r => !isHidden(r)).map((r) => {
            const has = (r.node.children || []).length > 0;
            const isCollapsed = collapsed[r.node.id];
            return (
              <div key={r.node.id} className="flex items-center gap-2 py-2" style={{ paddingLeft: r.level * 20 }}>
                {has ? (
                  <button onClick={() => toggle(r.node.id)} data-testid={`tree-toggle-${r.node.id}`} className="text-slate-400 hover:text-white">
                    {isCollapsed ? <ChevronRight size={14}/> : <ChevronDown size={14}/>}
                  </button>
                ) : <div className="w-4"/>}
                <div className="h-8 w-8 rounded-lg bg-[#070B14] border border-white/10 flex items-center justify-center"><User2 size={14} className="text-slate-400"/></div>
                <div className="flex-1 flex items-center gap-3 rounded-lg border border-white/5 bg-[#0F1626] px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{r.node.name} <span className="text-xs text-slate-500 ml-1">Lv {r.level}</span></div>
                    <div className="text-xs text-slate-500 font-mono truncate">{r.node.referral_code} · ${Number(r.node.active_investment||0).toFixed(2)}</div>
                  </div>
                  <div className="text-[10px] uppercase font-bold tracking-widest" style={{color: RANK_COLORS[r.node.rank] || "#94A3B8"}}>{r.node.rank}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
